import StructureAPI from "../../../../../store/reducers/builderReducers/structure/structureApi";
import { Template } from "../../../../../store/reducers/builderReducers/templates/templateApi";
import {
  getTemplateSourceComponent,
  getTemplateListKey,
} from "../../../../../store/reducers/builderReducers/templates/templateHelpers";
import { useAppSelector } from "../../../../../store/hooks";
import { AllComponents } from "../../../../_default/interfaces/base";
import { getComponentType } from "../../../../../utils/Helpers";
import { useMemo, useState } from "react";
import React from "react";
import { getLegacyWebpageIdForStructure } from "../../../../../store/reducers/builderReducers/pages/pageIdentity";
import type { Page } from "../../../../../store/reducers/builderReducers/pages/pagesTypes";

interface Props {
  component: AllComponents | undefined;
  /** 1-based insert index for {@link StructureAPI.injectTemplateComponent} (same semantics as addComponent). */
  sort_order: number;
  /**
   * When adding a root-level template (no parent), reorder roots so the injected block
   * sits after this component key — mirrors sibling row + button behavior.
   */
  insertAfterWebpageComponentRef?: string | number | null;
}

export default function SelectTemplate({
  component,
  sort_order,
  insertAfterWebpageComponentRef,
}: Props) {
  const templates = useAppSelector((state) => state.builder.templates);
  const componentType = component ? getComponentType(component) : null;
  const [isLoading, setIsLoading] = useState<string | number>(0);
  const activePageUuid = useAppSelector((state) => state.builder.pages.active);
  const pagesList = useAppSelector((state) => state.builder.pages.list) as Page[];
  const structureNumericId = useMemo(
    () =>
      getLegacyWebpageIdForStructure(
        pagesList,
        activePageUuid as string | null
      ),
    [pagesList, activePageUuid]
  );
  let allowedComponentTypes: Array<String> = [];
  switch (componentType) {
    case "row":
    case "header":
    case "footer":
    case "inner_section":
      // Col templates are section roots with column classes; rows and inner-sections
      // host columns the same way. Header/footer often wrap columns directly (no row
      // parent), so treat them like rows for template picking.
      allowedComponentTypes = ["col"];
      break;
    case "col":
      allowedComponentTypes = [
        "text",
        "ProductsComponent",
        "ProductDetailComponent",
        "Checkout",
        "Button",
        "form",
        "inner_section",
        "image",
      ];
      break;
    case null:
      allowedComponentTypes = ["row"];
      break;

    default:
      allowedComponentTypes = [];
      break;
  }

  function onTemplateClick(template: Template) {
    if (!activePageUuid) return;

    const source = getTemplateSourceComponent(template);
    if (!source) {
      console.error("Template has no embedded component data:", template.name);
      return;
    }

    setIsLoading(getTemplateListKey(template));
    const webpageRef = structureNumericId ?? activePageUuid;

    StructureAPI.injectTemplateComponent(webpageRef, source, {
      parent_id: null,
      parent_uuid: component?.uuid ?? null,
      sort_order,
      ...(insertAfterWebpageComponentRef != null &&
      String(insertAfterWebpageComponentRef).trim() !== ""
        ? { insertAfterWebpageComponentRef }
        : {}),
    })
      .then(() => {
        setIsLoading(0);
      })
      .catch((error) => {
        console.error("Error injecting template:", error);
        setIsLoading(0);
      });
  }

  const filteredTemplates = templates
    .filter((template) => {
      const source = getTemplateSourceComponent(template);
      return !!source && allowedComponentTypes.includes(getComponentType(source));
    })
    .sort((a, b) => {
      return a.name
        .toLocaleLowerCase()
        .localeCompare(b.name.toLocaleLowerCase());
    });

  return (
    <ul className="m-0 p-0">
      <div className="select-element-type__container">
        <div className="select-element-type__list">
          {filteredTemplates.map((template) => (
              <div
                key={getTemplateListKey(template)}
                style={{
                  cursor: isLoading !== 0 ? "not-allowed" : "pointer",
                }}
                className="select-element-type__list__item "
                onClick={() => {
                  if (isLoading) {
                    return;
                  }
                  onTemplateClick(template);
                }}
              >
                {template.name}

                {isLoading === getTemplateListKey(template) && (
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                    style={{ marginLeft: "5px" }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                )}
              </div>
            ))}

          {Array.from(filteredTemplates).length === 0 && (
            <div style={{ fontSize: "12px", textWrap: "nowrap" }}>
              0 templates found.
            </div>
          )}
        </div>
      </div>
    </ul>
  );
}
